import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {Vendor, VendorSortKey} from "@/repositories/vendors.repository";
import {ReadAllResponse} from "@/interfaces";
import {inviteVendor, removeVendorFromOrg, searchVendors} from "./vendors.api";

interface VendorsState {
    vendors: Vendor[];
    loading: boolean;
    error: string | null;
    total: number;
    filters: {
        page: number;
        pageSize: number;
        name: string;
        service: string;
        sortKey: VendorSortKey;
        sortOrder: "asc" | "desc";
    };
    inviteOpen: boolean;
    inviteSaving: boolean;
    inviteError: string | null;
}

const initialState: VendorsState = {
    vendors: [],
    loading: false,
    error: null,
    total: 0,
    filters: {
        page: 1,
        pageSize: 10,
        name: "",
        service: "",
        sortKey: "name",
        sortOrder: "asc",
    },
    inviteOpen: false,
    inviteSaving: false,
    inviteError: null,
};

export const fetchVendors = createAsyncThunk<
    ReadAllResponse<Vendor>,
    void,
    { state: { vendors: VendorsState } }
>("vendors/fetch", async (_, {getState}) => {
    const {
        page,
        pageSize,
        name,
        service,
        sortKey,
        sortOrder,
    } = getState().vendors.filters;

    return await searchVendors(page, pageSize, name, service, sortKey, sortOrder);
});

export const removeVendor = createAsyncThunk<void, string>(
    "vendors/remove",
    async (id) => {
        await removeVendorFromOrg(id);
    }
);

export const sendVendorInvite = createAsyncThunk(
    "vendors/sendInvite",
    async (email: string) => await inviteVendor(email)
);

const vendorsSlice = createSlice({
    name: "vendors",
    initialState,
    reducers: {
        setPage(state, {payload}: PayloadAction<number>) {
            state.filters.page = payload;
        },
        setPageSize(state, {payload}: PayloadAction<number>) {
            state.filters.pageSize = payload;
        },
        setName(state, {payload}: PayloadAction<string>) {
            state.filters.name = payload;
        },
        setService(state, {payload}: PayloadAction<string>) {
            state.filters.service = payload;
        },
        setSortKey(state, {payload}: PayloadAction<VendorSortKey>) {
            if (state.filters.sortKey === payload) {
                state.filters.sortOrder = state.filters.sortOrder === "asc" ? "desc" : "asc";
            } else {
                state.filters.sortKey = payload;
                state.filters.sortOrder = "asc";
            }
        },
        setInviteOpen(state, {payload}: { payload: boolean }) {
            state.inviteOpen = payload;
            if (!payload) state.inviteError = null;
        }
    },
    extraReducers: (b) =>
        b.addCase(fetchVendors.pending, (s) => {
            s.loading = true;
        }).addCase(fetchVendors.fulfilled, (s, a) => {
            s.loading = false;
            s.vendors = a.payload.data;
            s.total = a.payload.count ?? 0;
        }).addCase(fetchVendors.rejected, (s, a) => {
            s.loading = false;
            s.error = a.error.message ?? "Failed to load vendors";
        }).addCase(removeVendor.pending, (s) => {
            s.loading = true;
        }).addCase(removeVendor.fulfilled, (s) => {
            s.loading = false;
        }).addCase(removeVendor.rejected, (s, a) => {
            s.loading = false;
            s.error = a.error.message ?? "Failed to remove vendors";
        }).addCase(sendVendorInvite.pending, (s) => {
            s.inviteSaving = true;
            s.inviteError = null;
        }).addCase(sendVendorInvite.fulfilled, (s) => {
            s.inviteSaving = false;
        }).addCase(sendVendorInvite.rejected, (s, a) => {
            s.inviteSaving = false;
            s.inviteError = a.error.message ?? "Failed to send invite";
        }),
});

export const {
    setPage,
    setPageSize,
    setName,
    setService,
    setSortKey,
    setInviteOpen,
} = vendorsSlice.actions;
export default vendorsSlice.reducer;